<template>
<div style="height: 100%;">
    <div id="main" v-show="!rotaAninhada">
        <container-loader v-if="isLoading && !$store.getters.isLoading"/>
        <div class="page">
            <div class="leads vertical-flow">
                <div class="listing-header">
                    <div class="title-breadcrumb">
                        <h1 class="title">
                            {{ $t("leads") }}
                            <a
                                class="pl-1"
                                v-if="hide.hideInfo"
                                @click="infoIsHidden()"
                            >
                                <b-icon
                                    v-if="!hide.infoIsHidden"
                                    icon="eye-off-outline"
                                    type="is-danger"
                                />
                                <b-icon
                                    v-if="hide.infoIsHidden"
                                    icon="eye-outline"
                                    type="is-success"
                                />
                            </a>
                        </h1>
                        <nav class="breadcrumb" aria-label="breadcrumbs">
                            <ul>
                                <li>
                                    <a class="a-inativo">
                                        {{ $t("vendas") }}
                                    </a>
                                </li>
                                <li>
                                    <a aria-current="page">
                                        {{ $t("leads") }}
                                    </a>
                                </li>
                            </ul>
                        </nav>
                    </div>
                </div>

                <div class="search-and-filter">
                    <div class="search-bar">
                        <search-bar
                            :onSearch="debouncedOnSearch"
                            tooltipLabel="msg-campo-busca-leads"
                        />
                    </div>

                    <div class="situacao-select">
                        <label v-text="$t('situacao')" />
                        <v-select
                            ref="situacaoInput"
                            class="is-100"
                            label="label"
                            :options="situacoes"
                            @input="onSituacaoChanged"
                            v-model="situacao"
                        >
                            <template slot="option" slot-scope="option">
                                {{ $t(option.label) }}
                            </template>
                            <template
                                slot="selected-option"
                                slot-scope="option"
                            >
                                {{ $t(option.label) }}
                            </template>
                            <template slot="no-options">{{
                                $t("sem-resultados")
                            }}</template>
                        </v-select>
                    </div>
                </div>

            <div class="registry-wrapper lps-table">
                <empty-table-message v-if="isEmpty" class="mt-4"/>
                <b-table
                    v-else
                    :data="leads"
                    class="data-table"
                    @sort="sortTable"
                    backend-sorting
                >
                    <b-table-column
                        v-for="(f, i) in fields"
                        :key="i"
                        :field="f.key"
                        :label="$t(f.key)"
                        :width="f.width"
                        class="td-center"
                        :sortable="f.sortable"
                        :custom-sort="f.sortFn"
                    >
                        <template v-slot:header>
                            <span v-if="['id','nome','cnpjcpf','datahora'].includes(f.key)" class="header">
                                {{ $t(f.key) }}
                            </span>
                            <span v-else-if="f.key == 'rsocial'" class="header">
                                {{ $t('rsocial-sobrenome') }}
                            </span>
                            <span v-else>
                                {{ $t('situacao') }}
                                <hr class="table-sep" />
                                {{ $t('origem') }}
                            </span>
                        </template>
                        <template v-slot="props">
                            <div class="status-lead" v-if="f.key == 'situacao'">
                                <span
                                    class="tag"
                                    :class="{
                                        'is-info': props.row.situacao == 0,
                                        'is-success': props.row.situacao == 3,
                                        'is-danger': props.row.situacao == 2,
                                    }"
                                >
                                    {{ getSituacao(props.row.situacao) }}
                                </span>
                                <span
                                    v-if="origensFV.includes(props.row.origem)"
                                    class="tag is-fv status-tag"
                                >
                                    {{ props.row.origem }}
                                </span>
                                <span
                                    v-else-if="origensB2B.includes(props.row.origem)"
                                    class="tag is-b2b status-tag"
                                >
                                    {{ props.row.origem }}
                                </span>
                                <span v-else class="tag is-warning status-tag">
                                    {{
                                        props.row.origem === 'sem-origem'
                                            ? $t(props.row.origem)
                                            : props.row.origem
                                    }}
                                </span>
                            </div>
                            <div v-else-if="f.key == 'cnpjcpf'" class="header">
                                <HideInfo
                                    v-if="!cleanHide"
                                    :value="props.row[f.key]"
                                    :hideInfo="hide.hideInfo"
                                    :infoIsHidden="hide.infoIsHidden"
                                    showPartInfoPosition="end"
                                    :showPartInfoLenght="4"
                                />
                            </div>
                            <div v-else-if="f.key == 'datahora'" class="header">
                                {{ props.row[f.key] | moment("L LT") }}
                            </div>
                            <div v-else-if="f.key == 'nome' || f.key == 'rsocial'" class="header">
                                <router-link
                                    v-if="permissao.read"
                                    :to="{ name: 'ven-leads-view', params: { id: props.row.id, leadName: props.row.nome, leadStatus: props.row.situacao } }"
                                >
                                    <pre>{{ props.row[f.key] }}</pre>
                                </router-link>
                                <span v-else>{{ props.row[f.key] }}</span>
                            </div>
                            <div v-else class="header">
                                {{ props.row[f.key] }}
                            </div>
                        </template>
                    </b-table-column>
                </b-table>
                <PaginationViewer
                    class="mb-0"
                    :resetPagination="pagination.resetPagination"
                    :totalItems="pagination.totalRows"
                    :perPageOptions="pagination.perPageOptions"
                    :optionStarted="pagination.optionStarted"
                    @change="updatePagination"
                />
            </div>
            </div>
        </div>
        </div>
        <router-view />
    </div>
</template>

<script>
import ContainerLoader from "@/components/content/loader/ContainerLoader";
import LeadsService from "@/services/vendas/LeadsService";
import HideInfo from "@/components/shared/HideInfo";
import { mapState } from "vuex";

export default {
    beforeRouteUpdate(to, from, next) {
        if (to.name.includes("ven-leads") && to.name != "ven-leads-list") {
            this.rotaAninhada = true;
            next();
        } else {
            this.rotaAninhada = false;
            this.carregarDados();
            next();
        }
    },
    components: {
        ContainerLoader,
        HideInfo,
    },
    data() {
        return {
            leads: [],
            situacoes: [
                { id: 0, label: "aguardando-aprovacao" },
                { id: 2, label: "rejeitados" },
                { id: 3, label: "aprovados" },
                { id: 4, label: "todos" },
            ],
            situacao: { id: 0, label: "aguardando-aprovacao" },
            service: new LeadsService(this.$http, this.$router),
            fields: [
                { key: "id", width: 60, sortable: true, sortFn: null },
                { key: "nome", width: null, sortable: true, sortFn: null },
                { key: "rsocial", width: null, sortable: true, sortFn: null },
                { key: "cnpjcpf", width: null, sortable: true, sortFn: null },
                { key: "datahora", width: null, sortable: true, sortFn: null },
                { key: "situacao", width: 115, sortable: true, sortFn: null },
            ],
            pagination: {
                resetPagination: false,
                perPageOptions: [10, 25, 50, 100],
                optionStarted: 0,
                perPage: null,
                totalRows: 0,
                currentPage: 1,
            },
            isLoading: false,
            isEmpty: false,
            cleanHide: true,
            debouncedOnSearch: null,
            delete: null,
            queryFilter: "",
            column: "descricao",
            order: "asc",
            permissao: {},
            origensFV: ['FV', 'WEBFV'],
            origensB2B: ['B2B MOBILE', 'B2B WEB'],

            //variáveis utilizadas para evitar requests desnecessárias ao servidor
            situacaoOld: { id: 0, label: "aguardando-aprovacao" },
            queryFilterOld: "",
        };
    },
    created() {
        if (this.$route.name.includes("ven-leads") && this.$route.name != "ven-leads-list") {
            this.rotaAninhada = true;
        } else {
            this.rotaAninhada = false;
        }

        this.pagination.perPage = this.pagination.perPageOptions[this.pagination.optionStarted];
        this.permissao = this.hasPermissionAccess(
            "vendas",
            "clientes",
            "leads"
        );
        this.debouncedOnSearch = this.debounce(this.onSearch, 500);

        if (!this.permissao.read) {
            this.$router.push({
                name: "acesso-nao-permitido",
                params: { tela: "leads" },
            });
        } else {
            this.carregarDados();
        }
    },
    watch: {
        situacao: {
            deep: true,
            handler: function (newVal, oldVal) {         
                this.situacaoOld = oldVal;
                this.filterFromSituacaoOrLoadData();
            },
        },
        queryFilter(newVal, oldVal){
            this.queryFilterOld = oldVal;
            this.filterFromQueryOrLoadData();
        }
    },
    computed: {
        ...mapState(["hide"]),
    },
    methods: {
        infoIsHidden() {
            this.$store.dispatch("infoIsHidden", {
                infoIsHidden: !this.hide.infoIsHidden,
            });
        },
        getSituacao(situacao) {
            switch (situacao) {
                case 0:
                    return this.$t('aguardando-aprovacao');
                case 2:
                    return this.$t('rejeitado');
                case 3:
                    return this.$t('aprovado');
                default:
                    return "---";
            }
        },
        onSearch(val) {
            this.queryFilter = this.removeAcentos(val);
            this.pagination.currentPage = 1;
        },
        filterFromQueryOrLoadData(){
            /*  otimização descrita em: 
                https://project.sovis.com.br/projects/sovisb2b/wiki/Estrat%C3%A9gia_de_redu%C3%A7%C3%A3o_de_queries_e_requests_para_tabelas_com_grande_n%C3%BAmero_de_registros
            */

            //se o novo termo de busca é uma continuação do termo anterior
            if(this.queryFilter.includes(this.queryFilterOld) && this.pagination.totalRows < this.pagination.perPage){
                this.isLoading = true;
                this.cleanHide = true;

                this.leads = this.leads.filter(f => 
                    f.cnpjcpf.match(this.queryFilter) ||
                    f.cnpjcpf.replaceAll(/[^0-9]/g,'').match(this.queryFilter) ||
                    f.nome.match(this.queryFilter) ||
                    f.rsocial.match(this.queryFilter)
                );

                //timeout utilizado para renderizar novamente o componente de ocultação da LGPD
                setTimeout(() => { 
                    this.cleanHide = false;
                    this.isLoading = false; 
                }, 50);  
            }else{
                this.carregarDados();
            }
        },
        onSituacaoChanged(val) {
            this.situacao = val ? this.situacoes.filter((v) => v.id == val.id)[0] : this.situacoes[0];
        },
        filterFromSituacaoOrLoadData(){
            /*  otimização descrita em: 
                https://project.sovis.com.br/projects/sovisb2b/wiki/Estrat%C3%A9gia_de_redu%C3%A7%C3%A3o_de_queries_e_requests_para_tabelas_com_grande_n%C3%BAmero_de_registros
            */

            //se a situação anterior era "todos" com todos os registros sendo exibidos na página
            if(this.situacaoOld.id == 4 && this.pagination.totalRows < this.pagination.perPage){
                this.isLoading = true;
                this.cleanHide = true;
                this.leads = this.leads.filter(f => f.situacao == this.situacao.id);

                //timeout utilizado para renderizar novamente o componente de ocultação da LGPD
                setTimeout(() => { 
                    this.cleanHide = false;
                    this.isLoading = false; 
                }, 50);    
            }else{
                this.carregarDados();
            }
        },
        updatePagination(value) {
            this.pagination.resetPagination = false;
            this.pagination.perPage = value.perPage;
            this.pagination.currentPage = value.currentPage;
            this.carregarDados(value.currentPage);
        },
        carregarDados(currentPage = 1) {
            this.cleanHide = true;
            if (currentPage == 1 && this.pagination.currentPage != 1)
                this.pagination.resetPagination = true;
            this.isLoading = true;
            this.service
                .findAll(
                    this.column,
                    this.order,
                    currentPage - 1,
                    this.pagination.perPage,
                    this.situacao.id,
                    this.queryFilter
                )
                .then(this.mapItems)
                .then((value) => {
                    this.leads = value;
                    this.isEmpty = this.leads.length <= 0;
                })
                .catch((e) => {
                    this.isEmpty = true;
                    this.$root.showMessage("error", "msg-erro-load");
                    console.error("Erro ao carregar leads.");
                })
                .finally((_) => {
                    this.cleanHide = false;
                    this.isLoading = false;
                    this.pagination.resetPagination = false;
                });
        },
        mapItems(body) {
            this.pagination.totalRows = body.totalElements;
            return body.content.map((m) => {
                return {
                    id: m.idClienteProspeccao,
                    cnpjcpf: m.cnpjcpf,
                    nome: m.nome,
                    rsocial: m.rsocial,
                    datahora: m.dataHoraInc,
                    situacao: m.replicapda,
                    origem: m.origem,
                };
            });
        },
        sortTable(field, order) {
            this.column = field;
            this.order = order;
            this.carregarDados();
        },
    },
};
</script>
<style lang="scss" scoped>
#main {
    padding: 0 20px;
    height: auto;
    min-height: calc(100% - 50px);
    position: relative;

    .status-tag {
        margin-top: 6px;
    }

    .is-b2b {
        background-color: #ff6433;
        color: #fff;
    }

    .is-fv {
        background-color: #673ab7;
        color: #fff;
    }

    .lps-table {
        padding-top: 0px;
        font-size: 14px;
    }

    .status-lead {
        font-weight: 600;

        .tag {
            width: 100%;
        }
    }

    .header { //nome alternativo não funcionou
        display: inline-flex;
        align-items: center;
        z-index: 0;
    }

    .page {
        position: relative;
        padding-left: 0;


        .table-tag {
            width: 5rem !important;
        }

        .leads .search-and-filter {
            display: flex;
            justify-content: space-between;
            margin: 10px 0;

            .search-bar {
                width: 80%;
                padding-right: 10px;
            }
            .situacao-select {
                width: 30%;
                div {
                    height: 40px;
                }
            }
            .filter-bar {
                width: 31%;
            }
            @media (max-width: 760px) {
                .search-bar {
                    padding-right: 0;
                }
                .situacao-select {
                    width: 100%;
                }
            }
        }
    }
}
</style>
